import { ContentCopy } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import Loading from 'components/common/Loading';
import OverlineLabel from 'components/common/OverlineLabel';
import PhoneControl from 'components/formik/PhoneControl';
import Switch from 'components/formik/Switch';
import copy from 'copy-to-clipboard';
import { Field, Form, useFormikContext } from 'formik';
import AccountIcon from 'mdi-react/AccountIcon';
import ShieldAccountIcon from 'mdi-react/ShieldAccountIcon';
import { bool, func, node, object, string } from 'prop-types';
import React, { useCallback, useState } from 'react';
import { I18n } from 'utils/i18n';

const ReadOnlyIdField = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (!value) return;
    copy(value);
    setCopied(true);
  }, [value]);
  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        height: 40,
        pl: 1.75,
        pr: 0.5,
        borderRadius: 1,
      }}
    >
      <Typography
        component="span"
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          fontFamily: 'monospace',
          fontSize: 14,
          color: 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          userSelect: 'all',
        }}
      >
        {value}
      </Typography>
      <Tooltip
        title={I18n.t(copied ? 'UserDrawer.Copied' : 'UserDrawer.Copy')}
        onClose={() => setCopied(false)}
        leaveDelay={copied ? 1000 : 0}
      >
        <span>
          <IconButton size="small" onClick={handleCopy} disabled={!value}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Paper>
  );
};

ReadOnlyIdField.propTypes = { value: string.isRequired };

const Section = ({ label, children, sx }) => (
  <Box sx={{ mt: 2.5, ...sx }}>
    <OverlineLabel sx={{ display: 'block', mb: 0.75, lineHeight: 1.4 }}>{label}</OverlineLabel>
    {children}
  </Box>
);

Section.propTypes = {
  label: string.isRequired,
  children: node.isRequired,
  sx: object,
};

const RoleSelector = ({ disabled }) => {
  const { values, setFieldValue } = useFormikContext();
  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      color="primary"
      value={values.role ?? null}
      disabled={disabled}
      onChange={(_, next) => {
        if (next != null) setFieldValue('role', next);
      }}
      sx={{
        bgcolor: 'background.default',
        '& .MuiToggleButton-root': {
          flex: 1,
          gap: 1,
          py: 1.25,
          textTransform: 'none',
          fontWeight: 500,
        },
      }}
    >
      <ToggleButton value={1}>
        <AccountIcon size={18} />
        {I18n.t('UserDrawer.User')}
      </ToggleButton>
      <ToggleButton value={2}>
        <ShieldAccountIcon size={18} />
        {I18n.t('UserDrawer.Superadmin')}
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

RoleSelector.propTypes = { disabled: bool.isRequired };

const ToggleRow = ({ name, title, description = '', isLoading }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      py: 1.5,
      px: 2,
    }}
  >
    <Box sx={{ minWidth: 0, pr: 1 }}>
      <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mt: 0.25,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
    <Field name={name} component={Switch} color="primary" label={title} disabled={isLoading} />
  </Box>
);

ToggleRow.propTypes = {
  name: string.isRequired,
  title: string.isRequired,
  description: string,
  isLoading: bool.isRequired,
};

export const UserForm = ({ user = null, onClose, isLoading, title }) => {
  return (
    <Form
      autoComplete="off"
      data-lpignore="true"
      data-1p-ignore="true"
      data-bwignore="true"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box sx={{ flex: '1 1 auto', overflowY: 'auto', pl: 2.5, pr: 2.5, pt: 2.5, pb: 2 }}>
        <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {user?._id && (
          <Section label={I18n.t('UserDrawer.Id')} sx={{ mt: 1.5 }}>
            <ReadOnlyIdField value={user._id} />
          </Section>
        )}

        <Section label={I18n.t('UserDrawer.PhoneNumber')}>
          <Field
            name="phone"
            component={PhoneControl}
            country="es"
            specialLabel={false}
            inputProps={{
              type: 'tel',
              role: 'presentation',
              autoComplete: 'off',
              // Override the underlying <input name>/<input id> so password managers
              // don't classify this as a phone/login field (Formik tracks state by
              // its own field name, so changing the HTML name is safe).
              name: 'omn-record-num',
              id: 'omn-record-num',
              'data-1p-ignore': 'true',
              'data-lpignore': 'true',
              'data-bwignore': 'true',
              'data-form-type': 'other',
            }}
            disabled={isLoading}
            fullWidth
          />
        </Section>

        <Section label={I18n.t('UserDrawer.SystemRole')}>
          <RoleSelector disabled={isLoading} />
        </Section>

        <Section label={I18n.t('UserDrawer.Access')}>
          <Paper variant="outlined" sx={{ borderRadius: 1.5 }}>
            <ToggleRow
              name="isApp"
              title={I18n.t('UserDrawer.isApp')}
              description={I18n.t('UserDrawer.isAppDescription')}
              isLoading={isLoading}
            />
            <Divider />
            <ToggleRow
              name="isReadOnly"
              title={I18n.t('UserDrawer.isReadOnly')}
              description={I18n.t('UserDrawer.isReadOnlyDescription')}
              isLoading={isLoading}
            />
          </Paper>
        </Section>
      </Box>

      <Box
        component="footer"
        sx={{
          p: 1,
          flex: '0 0 auto',
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Button onClick={onClose} variant="outlined" color="secondary">
          {I18n.t('UserDrawer.Cancel')}
        </Button>
        <Button disabled={isLoading} type="submit" variant="contained" color="primary">
          {I18n.t('UserDrawer.Save')}
          {isLoading && <Loading sx={{ ml: 1 }} size={14} color="secondary" />}
        </Button>
      </Box>
    </Form>
  );
};

UserForm.propTypes = {
  user: object,
  onClose: func.isRequired,
  isLoading: bool.isRequired,
  title: string.isRequired,
};
